<template>
    <div :key="renderKey">
      <div class="row">
        <el-table v-if="options.length" :data="options" role="table"
          aria-label="User Documents Table">
          <el-table-column v-if ="!editable" :min-width="105" align="left" label="Actions">
            <div slot-scope="props">
              <el-tooltip
                content="Delete Document"
                class="display-inline"
                :open-delay="300"
                effect="light"
                placement="top"
              >
                <a
               
                  href="##"
                  @click.stop.prevent="handleDelete(props.row.id, props.row.name)"
                  class="btn-link btn-danger"
                >
                  <i class="tim-icons icon-trash-simple"></i>
                  <span class="hide-if-small-screensize">Delete</span>
                </a>
              </el-tooltip>
            </div>
          </el-table-column>
  
          <el-table-column
            v-for="column in tableColumns"
            :key="column.label"
            :min-width="column.minWidth"
            sortable
            :formatter="column.formatter"
            :prop="column.prop"
            :label="column.label"
          >
          </el-table-column>
        </el-table>
      </div>
      <div class="row align-center">
        <user-documents-modal 
          ref="userDocsModal" 
          @reloadDocuments="reRenderDocuments"
          :is-transaction-completion="isTransactionCompletion" 
          :email="email"
          :isNew="true"
        >
          <template v-slot:modal-link>
            <base-button type="primary" class="animation-on-hover">
                <i class="tim-icons icon-cloud-upload-94"></i> Choose Files
            </base-button>
          </template>
        </user-documents-modal>
      </div>
    </div>
  </template>
  
<script>
  import { Table, TableColumn } from "element-ui";
  import { BaseSwitch, BaseButton, Modal } from "@/components";
  import UserDocumentsModal from "./UserDocumentsModal.vue";
  import Swal from 'sweetalert2'; // new import for Swal latest version
  
  export default {
    name: "UserFilesUpload", // UserFilesUpload - this is the actual name
    components: {
      [Table.name]: Table,
      [TableColumn.name]: TableColumn,
      BaseSwitch,
      BaseButton,
      Modal,
      UserDocumentsModal
    },
  
    props: {
      email: {
        type: String,
        default: null,
      },
      editable:{
        type: Boolean,
        default: false
      },
      isTransactionCompletion:{
      type: Boolean,
      default: false
      }
    },
    data: () => ({
      options: [],
      tempIds: [],
      // currentDocument: null, // Not strictly needed if we're directly passing document
      renderKey: 0,
      maxFileNumber: 20,
      tableColumns: [
        {
          prop: "name",
          label: "NAME",
          minWidth: 100,
          scope: "col"
        },
        {
          prop: "type",
          label: "TYPE",
          minWidth: 100,
          scope: "col"
        },
      ],
    }),
    watch: {
      email(newEmail, oldEmail) {
        if (newEmail !== oldEmail) {
          // this.reRenderDocuments(); 
        }
      },
    },
    mounted() {
      this.options = [];
    },
    methods: {
      reRenderDocuments(fileFromModal) {
        // This method is called when UserDocumentsModal emits 'reloadDocuments'
        // fileFromModal should have the full 'type' object.
        this.addFiles(fileFromModal); 
      },
      async deleteAndRerender(fileId) {
        this.options = this.options.filter(o => o.id !== fileId);
        this.tempIds = this.tempIds.filter(i => i !== fileId);
        this.renderKey++;
      },
      handleDelete(fileId, nameOfFile) {
        Swal.fire({ 
          title: "Delete File",
          html: `Do you want to delete <strong>${nameOfFile}</strong>?`,
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#10b45a",
          cancelButtonColor: "red",
          confirmButtonText: "Yes, delete it!",
          reverseButtons: true
        }).then((result) => {
          if (result.isConfirmed) { // Use isConfirmed for modern Swal
            this.deleteAndRerender(fileId);
            const remainingFileTypes = this.options.map(f => f.type);
            this.$emit('fileDelete', { 
                deletedFileId: fileId, 
                deletedFileName: nameOfFile, 
                remainingFileTypes 
            });
          }
        });
      },
      restorePageScrolling() {
        this.$nextTick(() => {
          // Remove any scroll-blocking classes
          document.body.classList.remove('swal2-shown', 'swal2-height-auto', 'modal-open', 'no-scroll');
          
          // Force restore scrolling
          document.body.style.overflow = '';
          document.body.style.overflowY = 'auto';
          document.documentElement.style.overflow = '';
          document.documentElement.style.overflowY = 'auto';
          
          //console.log('Page scrolling restored after file upload');
        });
      },
      checkFile(processedDocument) {
        try {
          // This method emits the event that Register.vue listens to.
          if (!processedDocument || !processedDocument.type || typeof processedDocument.type.name === 'undefined') {
            //console.error("UserFilesUpload.vue: checkFile called with invalid document.type", processedDocument);
            return;
          }
          
          this.$emit('userFileUpload', processedDocument);
          
          // Force restore scrolling after emitting the upload event
          setTimeout(() => {
            this.restorePageScrolling();
          }, 300); // Small delay to wait for parent to complete processing
          
        } catch (error) {
          console.error('Error in checkFile:', error);
          // Always restore scrolling even on error
          this.restorePageScrolling();
        }
      },
      // This method might be redundant if addFiles handles modal clearing
      clearDocument() { 
          if (this.$refs.userDocsModal) { // make sure to use the correct ref
            this.$refs.userDocsModal.clearDocument(false);
          }
      },
      getFileTypes() {
        return this.options.map(f => f.type);
      },
      getFiles() {
        return this.options.map((f) => ({ 
            file: f.file, 
            type: f.type, // This is the type NAME (string) from this.options
            remarks: f.remarks 
        }));
      },
      addFiles(documentToAdd) { 
        if (!documentToAdd || !documentToAdd.file) {
          this.$toast.error("No file selected to add.");
          return;
        }

        if (!documentToAdd.type || typeof documentToAdd.type.name === 'undefined') {
            this.$toast.error("File type is missing or invalid. Cannot add the file.");
            //console.error("UserFilesUpload.vue: addFiles - Invalid document type", documentToAdd.type);
            return;
        }

        if (this.options.length >= 40) {
          this.$toast.error("Cannot add more than 40 files in total.");
          return;
        }

        let tId;
        let attempts = 0;
        const MAX_ID_GENERATION_ATTEMPTS = (this.maxFileNumber * 10) || 200; // This can be adjusted based on requirements

        do {
          tId = Math.floor(Math.random() * ((this.maxFileNumber * 100) || 2000)) + 1;
          attempts++;
        } while (this.tempIds.includes(tId) && attempts < MAX_ID_GENERATION_ATTEMPTS);

        if (this.tempIds.includes(tId)) {
          this.$toast.error("Could not generate a unique ID for the file. Please try again.");
          this.restorePageScrolling(); // Restore scrolling on error
          return;
        }
        
        this.tempIds.push(tId);

        const newFileOption = {
          id: tId,
          name: documentToAdd.file.name,
          type: documentToAdd.type.name, // Store the type name
          file: documentToAdd.file,
          remarks: documentToAdd.remarks || ""
        };
        
        this.options.push(newFileOption);
        this.$toast.success(`File "${newFileOption.name}" added.`);
        
        const documentForEvent = {
            ...documentToAdd, // original file, remarks, full type object
            id: tId,          // the new client-side Id
        };
        
        this.checkFile(documentForEvent);

        // Clear the modal using the correct ref
        if (this.$refs.userDocsModal) {
          this.$refs.userDocsModal.clearDocument(false);
        }
        this.renderKey++;
      },
    },
  };
</script>
  
  <style scoped>
  /* This is for "Delete | Download" type of styling in AGATRS */
  
  @media only screen and (max-width: 1510px) {
    .hide-if-large-screensize {
      display: none;
    }
  }
  
  @media only screen and (max-width: 800px) {
    .hide-if-medium-screensize {
      display: none;
    }
  }
  </style>